import React, { Component } from 'react';
import { connect } from 'react-redux';
import { css } from 'react-emotion';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Spinner } from '@cimpress/react-components/lib/shapes/index';
import { MAX_INT } from '../../../constants/appConstants';
import { TermType } from '../../../constants/termTypes';
import { selectors as termSelectors, actions as termActions } from '../../../reducers/terms';
import ErrorRobot from '../../common/ErrorRobot';
import { SkuInfoShape } from '../createContract/shared/SkuCard/SkuCard';
import EditSkusButton from './EditSkusButton';
import ItemSkuPriceDisplay from './ItemSkuPriceDisplay';

const containerStyle = css`
  display: flex;
  height: 100%;
  padding: 0 15px;
`;

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  margin-bottom: 25px;
`;

const termSectionTitleStyle = css`
  border-bottom: 1px solid lightgray;
  margin-bottom: 15px;
  padding-bottom: 5px;
`;

export class TermItems extends Component {
  static defaultProps = {};

  static propTypes = {
    fetchItems: PropTypes.func.isRequired,
    termRevision: PropTypes.object.isRequired,
    setLatestRevision: PropTypes.func.isRequired,
    items: PropTypes.objectOf(SkuInfoShape.isRequired).isRequired,
    isItemsLoading: PropTypes.bool,
    itemsLoadingFailed: PropTypes.bool,
    termIsEditable: PropTypes.bool
  };

  state = {
    selectedSkuInfo: null
  };

  componentDidMount() {
    this.props.fetchItems();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { termRevision } = this.props;
    const { termRevision: prevTermRevision } = prevProps;
    if (!termRevision) {
      return;
    }

    const newTermId = termRevision.termId;
    const newTermRevisionId = termRevision.revisionId;
    const newTermRevisionItemsCount = termRevision.itemsCount;
    const prevTermId = prevTermRevision.termId;
    const prevTermRevisionId = prevTermRevision.revisionId;
    const prevTermRevisionItemsCount = prevTermRevision.itemsCount;
    if (
      newTermId !== prevTermId ||
      newTermRevisionId !== prevTermRevisionId ||
      newTermRevisionItemsCount !== prevTermRevisionItemsCount
    ) {
      this.props.fetchItems();
    }
  }

  render() {
    const { termRevision, setLatestRevision, items, isItemsLoading, itemsLoadingFailed, termIsEditable } = this.props;

    const loadingComponent = (
      <div className="loading-container">
        <Spinner />
        <h5>Loading {termRevision.itemsCount || ''} items...</h5>
      </div>
    );

    const loadingItemsErrorComponent = <ErrorRobot title={<h4>There was a problem loading items</h4>} />;
    const allItems = Object.values(items);
    const firstItemPriceModel = get(allItems, '[0].priceModel') || {};

    let termTypeIsEditable = false;
    if (termIsEditable) {
      switch (termRevision.type) {
        case TermType.LIST_PRICE:
        case TermType.SHIPPING_COST:
        case TermType.SHIPPING_PRICE:
        case TermType.FREE_SHIPPING:
          termTypeIsEditable = true;
          break;
        case TermType.SPECIAL_PRICE:
          // if term type is special price, check that all items have the same price model.
          const allItemsHaveSamePriceModel = Object.values(items).every(
            item => item.priceModel.id === firstItemPriceModel.id
          );
          termTypeIsEditable = allItemsHaveSamePriceModel;
          break;
        default:
          termTypeIsEditable = false;
      }
    }

    const showEditSkusButton = termIsEditable && termTypeIsEditable;

    const mainComponent = itemsLoadingFailed ? (
      loadingItemsErrorComponent
    ) : (
      <ItemSkuPriceDisplay termRevision={termRevision} items={items} />
    );

    return (
      <div className={containerStyle}>
        <div className={bodyStyle}>
          <h5 className={termSectionTitleStyle}>
            <span className="h5">SKUs</span>
            {showEditSkusButton && (
              <EditSkusButton
                key="edit"
                termRevision={termRevision}
                setLatestRevision={setLatestRevision}
                items={items}
                firstItemPriceModel={firstItemPriceModel}
              />
            )}
          </h5>
          {isItemsLoading ? loadingComponent : mainComponent}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { termRevision } = ownProps;
  return {
    items: termSelectors.getTermRevisionItemsWithSkuInfo(state, termRevision.termId, termRevision.revisionId),
    isItemsLoading: termSelectors.getIsTermRevisionItemsLoading(state),
    itemsLoadingFailed: termSelectors.getHasTermRevisionItemsLoadingFailed(state)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const { termRevision } = ownProps;
  return {
    fetchItems: () =>
      dispatch(
        termActions.fetchTermRevisionItems(
          termRevision.termId,
          termRevision.revisionId,
          true,
          MAX_INT,
          termRevision.type
        )
      )
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TermItems);
